import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Note } from '@/components/app/Badges';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { PageHeader } from '@/components/app/PageHeader';
import { ExampleDataNote, RefsErrorNote, TIME_STATUS_TONE } from '@/components/app/DriftUI';
import { DemoProfileSwitcher } from '@/components/app/DemoProfile';
import { TimeEntryCard, TimeEntrySheet, timeFormFromTemplate } from '@/components/app/TimeEntrySheet';
import { useDemoProfile } from '@/state/profile';
import { TIME_STATUS_LABELS, hoursText, listTimeEntries, sendDraftTimeEntries } from '@/lib/driftApi';
import { fmtNumber } from '@/domain/numbers';
import { addDays, eachDay, formatDateLong, formatDateShort, formatRangeShort, osloDateOf, weekdayOf } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Min dag: den ansattes egen flate. Dagens og ukens egne føringer med status, én tydelig handling («Før timer»),
  «samme som sist» og nylige valg som redigerbare snarveier (aldri automatisk innsending), og «Send alle utkast».
  Medarbeiderkonteksten er demoprofilen (tydelig merket) – ingen innlogging eller tilgangsstyring i previewen.
  Samme poster som kontoret ser i «Timer»; status utkast/sendt/returnert/godkjent er en forenklet eksempelstatus.
*/

const DAY_SHORT = ['Søn', 'Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør'];

/** Mandag i uka datoen tilhører. */
export function weekStartOf(date) {
  const wd = weekdayOf(date); // 0 = søndag
  return addDays(date, wd === 0 ? -6 : 1 - wd);
}

function dayTitle(date, today) {
  if (date === today) return 'I dag';
  if (date === addDays(today, -1)) return 'I går';
  if (date === addDays(today, 1)) return 'I morgen';
  return formatDateLong(date);
}

/** Små oppsummeringer for uka – linjer, ikke KPI-kort. */
function WeekLine({ label, value, tone, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5 text-[14px]" data-testid={testId}>
      <span className="text-[var(--hr-muted)]">{label}</span>
      <span className={cn('hr-num font-semibold', tone === 'amber' ? 'text-[var(--hr-amber)]' : 'text-[var(--hr-heading)]')}>{value}</span>
    </div>
  );
}

const DOT = { neutral: 'bg-[var(--hr-muted-2)]', navy: 'bg-[var(--hr-navy)]', amber: 'bg-[var(--hr-amber)]', green: 'bg-[var(--hr-green)]' };

export default function MinDagPage() {
  const { employee, employeeId, loading: profileLoading, error: profileError, retry: retryProfile } = useDemoProfile();
  const [params, setParams] = useSearchParams();
  const today = osloDateOf(new Date());
  const selected = params.get('dag') && /^\d{4}-\d{2}-\d{2}$/.test(params.get('dag')) ? params.get('dag') : today;
  const weekStart = weekStartOf(selected);
  const weekEnd = addDays(weekStart, 6);
  const days = useMemo(() => eachDay(weekStart, weekEnd), [weekStart, weekEnd]);

  const [week, setWeek] = useState(null);
  const [status, setStatus] = useState('loading');
  const [recent, setRecent] = useState([]);
  const [sheet, setSheet] = useState(null); // null | { entry } | { initial }
  const [sending, setSending] = useState(false);

  useEffect(() => {
    document.title = 'Min dag · Hardanger Rock';
  }, []);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      const [w, r] = await Promise.all([listTimeEntries({ employeeId, from: weekStart, to: weekEnd }), listTimeEntries({ employeeId, from: addDays(today, -35), to: today })]);
      setWeek(w);
      setRecent(r.entries);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [employeeId, weekStart, weekEnd, today]);

  useEffect(() => {
    load();
  }, [load]);

  // ?ny=1 åpner skjemaet direkte (f.eks. fra prosjekt eller snarvei).
  useEffect(() => {
    if (params.get('ny') && employeeId) {
      setSheet({ initial: { date: selected } });
      const next = new URLSearchParams(params);
      next.delete('ny');
      setParams(next, { replace: true });
    }
  }, [params, setParams, employeeId, selected]);

  const setDay = (date) => {
    const next = new URLSearchParams(params);
    if (date === today) next.delete('dag');
    else next.set('dag', date);
    setParams(next, { replace: true });
  };

  const entries = useMemo(() => week?.entries ?? [], [week]);
  const dayEntries = useMemo(() => entries.filter((e) => e.date === selected).sort((a, b) => a.fromTime.localeCompare(b.fromTime)), [entries, selected]);
  const byDay = useMemo(() => {
    const acc = {};
    for (const d of days) acc[d] = { hours: 0, statuses: new Set(), count: 0 };
    for (const e of entries) {
      if (!acc[e.date]) continue;
      acc[e.date].hours += e.hours;
      acc[e.date].count += 1;
      acc[e.date].statuses.add(e.status);
    }
    return acc;
  }, [entries, days]);
  const sums = week?.sums;
  const drafts = entries.filter((e) => e.status === 'utkast');
  const returned = entries.filter((e) => e.status === 'returnert');
  const dayHours = dayEntries.reduce((a, e) => a + e.hours, 0);

  // «Samme som sist»: siste føring (nyeste dato, sist opprettet). Nylige valg: unike kunde/prosjekt/aktivitet-kombinasjoner.
  const last = recent[0] ?? null;
  const recentChoices = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const e of recent) {
      const key = `${e.projectId}|${e.subprojectId ?? ''}|${e.activityId}|${e.machineId ?? ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (e.project?.status === 'avsluttet' || e.employee?.active === false) continue;
      out.push(e);
      if (out.length >= 4) break;
    }
    return out.filter((e) => e !== last).slice(0, 3);
  }, [recent, last]);

  const openNew = () => setSheet({ initial: { date: selected } });
  const openFrom = (e) => setSheet({ initial: timeFormFromTemplate(e, selected, employeeId) });
  const openEntry = (e) => setSheet({ entry: e });

  const sendAll = async () => {
    setSending(true);
    try {
      const r = await sendDraftTimeEntries({ employeeId, from: weekStart, to: weekEnd, actorEmployeeId: employeeId });
      toast.success(`${r.sent} ${r.sent === 1 ? 'utkast sendt inn' : 'utkast sendt inn'}`, { description: 'Kontoret ser føringene i Timer.' });
      load();
    } catch (err) {
      toast.error('Kunne ikke sende utkastene', { description: err.message });
    } finally {
      setSending(false);
    }
  };

  const summaryLine = sums
    ? `${dayTitle(selected, today)}: ${dayEntries.length ? `${dayEntries.length} ${dayEntries.length === 1 ? 'føring' : 'føringer'} · ${hoursText(dayHours)}` : 'ingen føringer'}${drafts.length ? ` · ${drafts.length} utkast i uka` : ''}${returned.length ? ` · ${returned.length} returnert` : ''}`
    : 'Henter dine føringer …';

  const primary = (
    <Button onClick={openNew} disabled={!employeeId} className="h-12 w-full rounded-[12px] bg-[var(--hr-heading)] text-[16px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)] lg:h-11" data-testid="min-dag-primary-log-time-button">
      <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Før timer
    </Button>
  );

  const sidePanel = (
    <div className="space-y-4">
      {primary}

      {last && (
        <Panel className="p-4" data-testid="min-dag-samme-som-sist-card">
          <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Samme som sist</div>
          <div className="mt-1 text-[15px] font-semibold leading-6 text-[var(--hr-heading)]">{last.project?.customer}</div>
          <div className="text-[14px] text-[var(--hr-text)]">
            {last.project?.name}{last.subproject ? ` · ${last.subproject.name}` : ''} · {last.activity?.label}
          </div>
          <div className="hr-tabular mt-0.5 text-[13px] text-[var(--hr-muted)]">
            {last.fromTime}–{last.toTime}{last.breakMinutes ? ` · pause ${last.breakMinutes} min` : ''}{last.machine ? ` · ${last.machine.internalNo}` : ''} · sist {formatDateShort(last.date)}
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" className="h-10 flex-1 rounded-[10px] text-[14px]" onClick={() => openFrom(last)} disabled={last.project?.status === 'avsluttet'} data-testid="min-dag-samme-som-sist-use-button">
              Bruk som utgangspunkt
            </Button>
          </div>
          <p className="mt-2 text-[12px] text-[var(--hr-muted-2)]">Åpner skjemaet ferdig utfylt – du sjekker og sender selv.</p>
          {recentChoices.length > 0 && (
            <div className="mt-3 border-t border-[var(--hr-hairline)] pt-3">
              <div className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]">Nylige valg</div>
              <ul className="space-y-1" data-testid="min-dag-recent-choices">
                {recentChoices.map((e) => (
                  <li key={e.id}>
                    <button type="button" onClick={() => openFrom(e)} className="flex min-h-[40px] w-full items-center justify-between gap-2 rounded-[8px] px-2 py-1.5 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`min-dag-recent-${e.id}`}>
                      <span className="min-w-0">
                        <span className="block truncate text-[14px] font-medium text-[var(--hr-heading)]">{e.project?.customer} · {e.project?.name}</span>
                        <span className="block truncate text-[13px] text-[var(--hr-muted)]">{e.activity?.label}{e.subproject ? ` · ${e.subproject.name}` : ''}{e.machine ? ` · ${e.machine.internalNo}` : ''}</span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[var(--hr-muted-2)]" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Panel>
      )}

      <Panel className="p-4">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-[8px]" onClick={() => setDay(addDays(selected, -7))} aria-label="Forrige uke" data-testid="min-dag-week-prev">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <div className="text-center">
            <div className="text-[14px] font-semibold text-[var(--hr-heading)]">Uke {isoWeek(weekStart)}</div>
            <div className="hr-tabular text-[13px] text-[var(--hr-muted)]">{formatRangeShort(weekStart, weekEnd)}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9 rounded-[8px]" onClick={() => setDay(addDays(selected, 7))} disabled={addDays(weekStart, 7) > today} aria-label="Neste uke" data-testid="min-dag-week-next">
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <div className="mt-3 grid min-w-0 grid-cols-7 gap-1" data-testid="min-dag-week-strip">
          {days.map((d) => {
            const info = byDay[d];
            const active = d === selected;
            const isToday = d === today;
            const future = d > today;
            return (
              <button
                key={d}
                type="button"
                onClick={() => setDay(d)}
                aria-pressed={active}
                aria-label={`${formatDateLong(d)}: ${info?.count ? hoursText(info.hours) : 'ingen føringer'}`}
                className={cn(
                  'flex min-h-[64px] min-w-0 flex-col items-center justify-start gap-0.5 overflow-hidden rounded-[10px] border px-0.5 py-1.5 text-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]',
                  active ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-transparent hover:bg-[var(--hr-surface-2)]',
                  future && !active && 'opacity-50',
                )}
                data-testid={`min-dag-week-day-button-${d}`}
              >
                <span className={cn('text-[11px] font-medium uppercase tracking-wide', active ? 'text-white/80' : 'text-[var(--hr-muted-2)]')}>{DAY_SHORT[weekdayOf(d)]}</span>
                <span className={cn('hr-num text-[14px] font-semibold leading-5', !active && isToday && 'underline decoration-[var(--hr-accent)] decoration-2 underline-offset-4', active ? 'text-white' : 'text-[var(--hr-heading)]')}>{Number(d.slice(8, 10))}</span>
                <span className={cn('hr-num text-[12px]', active ? 'text-white/90' : info?.count ? 'text-[var(--hr-text)]' : 'text-[var(--hr-muted-2)]')}>{info?.count ? fmtNumber(info.hours, 1) : '–'}</span>
                <span className="flex h-1.5 items-center gap-0.5" aria-hidden="true">
                  {[...(info?.statuses ?? [])].map((s) => (
                    <span key={s} className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-white/80' : DOT[TIME_STATUS_TONE[s]] ?? DOT.neutral)} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 divide-y divide-[var(--hr-hairline)] border-t border-[var(--hr-hairline)]" data-testid="min-dag-week-mini-stats">
          <WeekLine label="Denne uka" value={sums ? hoursText(sums.hours) : '–'} testId="min-dag-week-hours" />
          {sums?.machineHours > 0 && <WeekLine label="Maskintimer" value={hoursText(sums.machineHours)} testId="min-dag-week-machine" />}
          {Object.entries(TIME_STATUS_LABELS).map(([k, label]) => {
            const n = sums?.byStatus?.[k] ?? 0;
            if (!n) return null;
            return <WeekLine key={k} label={label} value={String(n)} tone={k === 'returnert' ? 'amber' : undefined} testId={`min-dag-week-status-${k}`} />;
          })}
        </div>
        {drafts.length > 0 && (
          <Button variant="outline" className="mt-3 h-11 w-full rounded-[10px] text-[14px]" onClick={sendAll} disabled={sending} data-testid="min-dag-send-all-drafts-button">
            <Send className="mr-1.5 h-4 w-4" aria-hidden="true" /> {sending ? 'Sender …' : `Send alle utkast (${drafts.length})`}
          </Button>
        )}
      </Panel>
    </div>
  );

  return (
    <div className="space-y-5">
      <PageHeader title="Min dag" lead={summaryLine} titleTestId="min-dag-page-title" leadTestId="min-dag-summary-line">
        <DemoProfileSwitcher className="sm:shrink-0" />
      </PageHeader>

      <ExampleDataNote>Fiktiv medarbeider og fiktive føringer. Status «utkast / sendt / returnert / godkjent» er en forenklet eksempelstatus – ikke kundens lønns- eller godkjenningsregler.</ExampleDataNote>

      {profileError && !employee && <RefsErrorNote error={profileError} loading={profileLoading} onRetry={retryProfile} what="medarbeidere" testId="min-dag-refs-error" />}

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="min-w-0 space-y-4 lg:order-2 lg:col-span-5 xl:col-span-4">{sidePanel}</div>

        <div className="min-w-0 lg:order-1 lg:col-span-7 xl:col-span-8">
          <Panel className="min-w-0">
            <PanelHeader
              title={dayTitle(selected, today)}
              description={selected === today ? formatDateLong(today) : selected > today ? 'Planlagt dag – føringer kan legges inn på forhånd.' : undefined}
              actions={
                dayEntries.length > 0 ? (
                  <span className="hr-tabular text-[14px] text-[var(--hr-muted)]" data-testid="min-dag-day-sum">
                    {dayEntries.length} {dayEntries.length === 1 ? 'føring' : 'føringer'} · <span className="font-semibold text-[var(--hr-heading)]">{hoursText(dayHours)}</span>
                  </span>
                ) : null
              }
            />
            {status === 'loading' && <div className="p-5"><SkeletonTable rows={3} /></div>}
            {status === 'error' && (
              <div className="p-5">
                <Note tone="amber" data-testid="min-dag-load-error">
                  Kunne ikke hente føringene dine. <Button variant="link" className="h-auto px-1" onClick={load} data-testid="min-dag-load-retry">Prøv igjen</Button>
                </Note>
              </div>
            )}
            {(status === 'ready' || status === 'refreshing') && dayEntries.length === 0 && (
              <div className="p-5" data-testid="min-dag-empty-state">
                <EmptyState type="no_results" title={selected === today ? 'Ingen føringer i dag' : `Ingen føringer ${formatDateShort(selected)}`} description="Trykk «Før timer» for å registrere første føring – eller bruk «samme som sist»." action={<Button onClick={openNew} className="h-11 rounded-[10px] bg-[var(--hr-heading)] text-white hover:bg-[var(--hr-navy)]" data-testid="min-dag-empty-new">Før timer</Button>} className="py-6" />
              </div>
            )}
            {(status === 'ready' || status === 'refreshing') && dayEntries.length > 0 && (
              <ul className="space-y-3 p-4" data-testid="min-dag-day-list">
                {dayEntries.map((e) => (
                  <TimeEntryCard key={e.id} entry={e} onOpen={openEntry} onCopy={openFrom} testPrefix="min-dag-entry" />
                ))}
              </ul>
            )}
          </Panel>

          <p className="mt-3 px-1 text-[13px] text-[var(--hr-muted)]">
            Kontoret ser de samme føringene i <Link to={`/timer?employeeId=${encodeURIComponent(employeeId)}&periode=egen&from=${weekStart}&to=${weekEnd}`} className="font-medium text-[var(--hr-heading)] underline-offset-2 hover:underline" data-testid="min-dag-to-timer">Timer</Link> – og kan godkjenne eller sende tilbake med beskjed.
          </p>
        </div>
      </div>

      <TimeEntrySheet open={!!sheet} onClose={() => setSheet(null)} entry={sheet?.entry ?? null} initial={sheet?.initial} context="employee" actorEmployeeId={employeeId} today={today} onChanged={() => load()} />
    </div>
  );
}

/** ISO-ukenummer for en mandag (YYYY-MM-DD). */
export function isoWeek(date) {
  const [y, m, d] = date.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
}
